import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { finalize, pairwise, startWith, Subject, Subscription, take } from 'rxjs';
import { Operation } from '../../enums/utils/operations.enum';
import { ILocationPostData } from '../../models/location/location-post-data.model';
import { ILocationUpdateData } from '../../models/location/location-update-data.model';
import { ILocation } from '../../models/location/location.model';
import { SelectedOrgService } from '../../services/selected-org.service';
import { OrgLocationsStoreService } from '../../services/store/org-locations-store.service';
import { UtilsService } from '../../services/utils.service';
import { ModalHeaderComponent } from '../components/modal-header/modal-header.component';
import { Modal } from '../modal';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ExternalHrefPipe } from '../../pipes/external-href.pipe';
import { IAddressFG, getAddressFG } from '../address-form-group-modal/address-form-group-modal.component';
import { DividerModule } from 'primeng/divider';
import { CountryDropdownInputComponent } from '../../components/country-dropdown-input/country-dropdown-input.component';
import { LocationCodeComponent } from './location-code/location-code.component';
import { GoogleCalendarSyncComponent } from '../../components/google-calendar-sync/google-calendar-sync.component';
import { InputTextareaSubtleComponent } from '../../components/subtle-inputs/input-textarea-subtle/input-textarea-subtle.component';
import { ILocationGroup } from '../../models/location-group/location.group.model';
import { DropdownModule } from 'primeng/dropdown';
import { ModalService } from '../../services/modal.service';
import { LocationGroupModalComponent } from '../location-group-modal/location-group-modal.component';
import { SelectItem } from 'primeng/api';
import { LocationGroupsService } from '../../services/entities/location-groups/location-groups.service';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

interface ILocationForm {
  code: FormControl<string | null>;
  title: FormControl<string>;
  internalTitle: FormControl<string | null>;
  note: FormControl<string | null>;
  // address: FormControl<string | null>;
  address: FormGroup<IAddressFG>;
  mapUrl: FormControl<string | null>;

  locationGroupId: FormControl<number | null>;
};

@Component({
  selector: 'app-location-modal',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, TranslateModule,
    ButtonModule, InputTextModule, InputTextareaModule,
    ModalHeaderComponent, InputGroupModule, InputGroupAddonModule, DropdownModule,
    ExternalHrefPipe, DividerModule, CountryDropdownInputComponent, LocationCodeComponent,
    GoogleCalendarSyncComponent, InputTextareaSubtleComponent, NgbTooltipModule
  ],
  templateUrl: './location-modal.component.html',
  styleUrls: ['./location-modal.component.scss']
})
export class LocationModalComponent extends Modal implements OnInit, OnDestroy {
  Operation = Operation;

  @Input() operation: Operation | undefined;
  @Input() location: ILocation | undefined;
  @Output() result = new EventEmitter<{ operation: Operation, data: ILocation }>()

  form = new FormGroup<ILocationForm>({
    code: new FormControl<string | null>(null),
    title: new FormControl('', { nonNullable: true, validators: [ Validators.required ]}),
    internalTitle: new FormControl('', { validators: [ Validators.required ]}),
    note: new FormControl(null),
    address: getAddressFG({}),
    mapUrl: new FormControl(null),

    locationGroupId: new FormControl<number | null>(null)
  });

  locationGroups: ILocationGroup[] = [];
  locationGroupOptions: SelectItem[] = [];

  saving: boolean = false;
  fetchingLocationGroups: boolean = false;
  locationGroupsFetched$ = new Subject<void>();

  subs: Subscription[] = [];

  constructor(
    private utilsService: UtilsService,
    private orgLocationsStoreService: OrgLocationsStoreService,
    private selectedOrgService: SelectedOrgService,
    private locationGroupsService: LocationGroupsService,
    private modalService: ModalService,
    private translate: TranslateService
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.fetchLocationGroups();

    if (this.operation === Operation.UPDATE && this.location) {
      this.form.patchValue({
        code: this.location.code,
        mapUrl: this.location.mapUrl,
        note: this.location.note,
        title: this.location.title,
        internalTitle: this.location.internalTitle,
        address: {
          buildingNumber: this.location.address.buildingNumber,
          city: this.location.address.city,
          country: this.location.address.country,
          countryCode: this.location.address.countryCode,
          street: this.location.address.street,
          zip: this.location.address.zip
        },

        locationGroupId: this.location.locationGroupId ?? null
      });
    }

    this.watchForm();
  }

  private watchForm() {
    this.subs.push(
      // Sync internalTitle with title if they are the same
      this.form.controls.internalTitle.valueChanges
        .pipe(
          startWith(this.form.controls.internalTitle.value), // Emit the initial value as the "previous" value on the first change
          pairwise() // Emits previous and current values as an array
        )
        .subscribe(([previousValue, currentValue]) => {
          const titleValue = this.form.value.title;

          if (previousValue === titleValue) {
            this.form.controls.title.setValue(currentValue ?? '', { emitEvent: false });
          }
        })
    )
  }

  onSubmit() {
    this.utilsService.markFormGroupDirty(this.form);
    if (this.form.invalid || this.saving) {
      console.error('invalid');
      return;
    }

    switch (this.operation) {
      case Operation.CREATE:
        this.create();
        break;
      case Operation.UPDATE:
        this.update();
        break;
      default:
        this.utilsService.logError('Operation not handled! 12jlkaksdfhgeiu9');
        break;
    }

  }
  create() {
    const selectedOrgId = this.selectedOrgService.getCurrentValue();
    if (!selectedOrgId) return;

    const formValue = this.form.getRawValue();
    const data: ILocationPostData = {
      title: formValue.title,
      internalTitle: formValue.internalTitle,
      code: formValue.code,
      address: formValue.address,
      note: formValue.note,
      mapUrl: formValue.mapUrl,

      organizationId: selectedOrgId,
      locationGroupId: formValue.locationGroupId
    };

    this.saving = true;
    this.orgLocationsStoreService.createLocation(data).pipe(
      finalize(() => this.saving = false)
    ).subscribe({
      next: (res) => {
        this.result.emit({ operation: Operation.CREATE, data: res });
        this.close();
      }
    });
  }
  update() {
    if (!this.location?.id) return;

    const formValue = this.form.getRawValue();
    const data: ILocationUpdateData = {
      id: this.location.id,
      code: formValue.code,
      title: formValue.title,
      internalTitle: formValue.internalTitle,
      address: formValue.address,
      note: formValue.note,
      mapUrl: formValue.mapUrl,

      locationGroupId: formValue.locationGroupId ?? null
    };

    this.saving = true;
    this.orgLocationsStoreService.updateLocation(data).pipe(
      finalize(() => this.saving = false)
    ).subscribe({
      next: (res) => {
        this.close();
      }
    });
  }

  private fetchLocationGroups() {
    const orgId = this.selectedOrgService.getCurrentValue();
    if (!orgId) return;

    this.fetchingLocationGroups = true;
    this.locationGroupsService.getAllLocationGroupsByOrgId(orgId).pipe(
      take(1)
    ).subscribe({
      next: (res) => {
        this.locationGroups = res;
        this.createLocationGroupOptions();
        this.locationGroupsFetched$.next();
        this.fetchingLocationGroups = false;
      },
      error: () => {
        this.fetchingLocationGroups = false;
      }
    })
  }
  private createLocationGroupOptions() {
    this.locationGroupOptions = this.locationGroups.map(x => ({
      label: x.title,
      value: x.id
    }));

    // Add "Default" option
    this.locationGroupOptions.unshift({
      label: this.translate.instant('admin.location-modal.default-location-group'),
      value: null
    });
  }

  onNewLocationGroup() {
    this.modalService.openLocationGroupModal(
      LocationGroupModalComponent,
      Operation.CREATE,
      (newLocationGroupId: number) => {
        this.fetchLocationGroups();
        this.locationGroupsFetched$.subscribe(() => {
          if (this.locationGroups.find(x => x.id === newLocationGroupId)) {
            this.form.controls.locationGroupId.setValue(newLocationGroupId);
          }
        })
      },
      undefined
    );
  }
  onUpdateLocationGroup(option: SelectItem, e: Event) {
    e.stopPropagation();
    
    const locationGroup = this.locationGroups.find(x => x.id === option.value);
    if (locationGroup) {
      this.modalService.openLocationGroupModal(
        LocationGroupModalComponent,
        Operation.UPDATE,
        (updatedLocationGroupId: number) => {
          this.fetchLocationGroups();
        },
        locationGroup
      )
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    
    this.subs.forEach(x => x.unsubscribe());
  }

}
